// Shared Business / Address / Bank field definitions + lightweight client-side
// format checks for the Add Partner dialog (index.tsx) and the partner profile
// edit sections (PartnerDetail.tsx) — both forms collect the exact same fields
// that the backend's businessSchema/addressSchema/bankSchema (onboarding.validators.js)
// validate, so the shapes here mirror those 1:1.

export const BUSINESS_TYPE_OPTIONS: { value: 'LIMITED_COMPANY' | 'SOLE_TRADER'; label: string }[] = [
  { value: 'LIMITED_COMPANY', label: 'Limited Company' },
  { value: 'SOLE_TRADER', label: 'Sole Trader' },
];

// partnerType is stored as free-text (not a Mongoose enum) so the mobile app
// and admin panel can each offer their own set — but where a value is shared
// between both, the admin must show the same label the mobile app's role
// picker used, or the same partner reads as a different role depending on
// which screen an admin is looking at. Mirrors the mobile app's ROLE_OPTIONS
// (Interior & Architect / E-Retailer / Dealer & Distributer / Field Agent /
// Other). Values with no mobile-side counterpart (admin-only categories)
// fall back to a prettified version of the raw enum value.
export const PARTNER_TYPE_LABELS: Record<string, string> = {
  ARCHITECT: 'Interior & Architect',
  MERCHANT: 'E-Retailer',
  PROPERTY_DEVELOPER: 'Dealer & Distributer',
  INSTALLER: 'Field Agent',
  Other: 'Other',
};

export const PARTNER_TYPE_OPTIONS = [
  'INSTALLER',
  'PLUMBER',
  'ELECTRICIAN',
  'ARCHITECT',
  'ESTATE_AGENT',
  'PROPERTY_DEVELOPER',
  'ENERGY_ASSESSOR',
  'MERCHANT',
  'Other',
];

export const partnerTypeLabel = (value?: string | null): string => {
  if (!value) return 'N/A';
  if (PARTNER_TYPE_LABELS[value]) return PARTNER_TYPE_LABELS[value];
  return value
    .toLowerCase()
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
};

export interface BusinessFormState {
  businessType: 'LIMITED_COMPANY' | 'SOLE_TRADER' | '';
  legalName: string;
  tradingName: string;
  companiesHouseNumber: string;
  utr: string;
  vatNumber: string;
}

export const emptyBusinessForm: BusinessFormState = {
  businessType: '',
  legalName: '',
  tradingName: '',
  companiesHouseNumber: '',
  utr: '',
  vatNumber: '',
};

export interface AddressFormState {
  postcode: string;
  line1: string;
  line2: string;
  town: string;
  county: string;
  uprn: string;
}

export const emptyAddressForm: AddressFormState = {
  postcode: '',
  line1: '',
  line2: '',
  town: '',
  county: '',
  uprn: '',
};

export interface BankFormState {
  accountHolderName: string;
  sortCode: string;
  accountNumber: string;
}

export const emptyBankForm: BankFormState = {
  accountHolderName: '',
  sortCode: '',
  accountNumber: '',
};

// Mirrors Utils/ukValidators.js UK_POSTCODE_REGEX.
const UK_POSTCODE_REGEX = /^(GIR ?0AA|[A-Z]{1,2}\d[A-Z\d]? ?\d[A-Z]{2})$/i;
export const isValidPostcode = (value: string) => UK_POSTCODE_REGEX.test(value.trim());

// Mirrors isValidCompaniesHouseNumber.
const COMPANIES_HOUSE_REGEX = /^(\d{8}|[A-Z]{2}\d{6})$/i;
export const isValidCompaniesHouseNumber = (value: string) => COMPANIES_HOUSE_REGEX.test(value.trim());

// Mirrors isValidUtr.
export const isValidUtr = (value: string) => /^\d{10}$/.test(value.trim());

// Mirrors isValidVatNumber.
const VAT_REGEX = /^GB(\d{9}|\d{12}|GD\d{3}|HA\d{3})$/i;
export const isValidVatNumber = (value: string) => VAT_REGEX.test(value.replace(/\s+/g, '').trim());

// Mirrors isValidSortCode (stored as digits-only, 6 chars).
export const isValidSortCode = (value: string) => /^\d{6}$/.test(value.replace(/\D/g, ''));

// Mirrors isValidAccountNumber.
export const isValidAccountNumber = (value: string) => /^\d{8}$/.test(value.trim());

export const validateBusinessForm = (form: BusinessFormState): string | null => {
  if (!form.businessType) return 'Business type is required';
  if (!form.legalName.trim()) return 'Legal name is required';
  if (form.businessType === 'LIMITED_COMPANY') {
    if (!form.companiesHouseNumber.trim()) return 'Companies House number is required for a limited company';
    if (!isValidCompaniesHouseNumber(form.companiesHouseNumber)) return 'Invalid Companies House number format';
  }
  if (form.businessType === 'SOLE_TRADER') {
    if (!form.utr.trim()) return 'UTR is required for a sole trader';
    if (!isValidUtr(form.utr)) return 'UTR must be exactly 10 digits';
  }
  if (form.vatNumber.trim() && !isValidVatNumber(form.vatNumber)) return 'Invalid UK VAT number format';
  return null;
};

export const validateAddressForm = (form: AddressFormState): string | null => {
  if (!form.postcode.trim() || !isValidPostcode(form.postcode)) return 'A valid UK postcode is required';
  if (!form.line1.trim()) return 'Address line 1 is required';
  if (!form.town.trim()) return 'Town is required';
  return null;
};

export const validateBankForm = (form: BankFormState): string | null => {
  if (!form.accountHolderName.trim()) return 'Account holder name is required';
  if (!isValidSortCode(form.sortCode)) return 'Sort code must be 6 digits';
  if (!isValidAccountNumber(form.accountNumber)) return 'Account number must be 8 digits';
  return null;
};

export const businessFormToPayload = (form: BusinessFormState) => ({
  businessType: form.businessType as 'LIMITED_COMPANY' | 'SOLE_TRADER',
  legalName: form.legalName.trim(),
  tradingName: form.tradingName.trim() || undefined,
  companiesHouseNumber:
    form.businessType === 'LIMITED_COMPANY' ? form.companiesHouseNumber.trim() : undefined,
  utr: form.businessType === 'SOLE_TRADER' ? form.utr.trim() : undefined,
  vatNumber: form.vatNumber.trim() || undefined,
});

export const addressFormToPayload = (form: AddressFormState) => ({
  postcode: form.postcode.trim(),
  line1: form.line1.trim(),
  line2: form.line2.trim() || undefined,
  town: form.town.trim(),
  county: form.county.trim() || undefined,
  uprn: form.uprn.trim() || undefined,
});

export const bankFormToPayload = (form: BankFormState) => ({
  accountHolderName: form.accountHolderName.trim(),
  sortCode: form.sortCode.replace(/\D/g, ''),
  accountNumber: form.accountNumber.trim(),
});
